import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UserRoles } from '../../redux/reducers/authenticationReducer';
import { addOrganizationUser, selectUserList } from '../../redux/reducers/organizationReducer';

const initialState = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  role: UserRoles.USER,
};

function AddUserModal({ handleClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [details, setDetails] = useState(initialState);
  const [validationError, setValidationError] = useState(false);
  const userList = useSelector(selectUserList);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...details };
    newDetails[name] = value;
    setDetails(newDetails);
  };

  const handleRoleChange = (event) => {
    const newDetails = {
      ...details,
      role: event.target.value,
    };
    setDetails(newDetails);
  };

  function validate(property, value) {
    switch (property) {
      case 'email':
        return value !== '' && !userList.find((person) => person.email === value.toLowerCase());
      default:
        return false;
    }
  }

  const handleSave = () => {
    if (validate('email', details.email)) {
      setValidationError(false);
      const email = details.email.toLowerCase();
      dispatch(
        addOrganizationUser({
          details: { ...details, email },
          role: details.role,
        }),
      );
      handleClose();
    } else {
      setValidationError(true);
    }
  };

  const handleCancel = () => {
    setValidationError(false);
    setDetails(initialState);
    handleClose();
  };

  return (
    <Modal open onClose={handleSave}>
      <Container variant="settingsModal" sx={{ display: 'flex', flexDirection: 'column' }} disableGutters>
        <Container variant="formContainer" disableGutters>
          <Stack direction="column" spacing={2}>
            <Typography variant="h6">{t('person.user.add')}</Typography>
            <Typography variant="body">{t('person.user.addDescription')}</Typography>
            <TextField
              id='email'
              variant="outlined"
              subtype="signinField"
              onChange={handleChange}
              name="email"
              defaultValue={details.email}
              InputLabelProps={{
                shrink: true,
              }}
              label={t('person.modal.email')}
              required
              error={validationError && !validate('email', details.email)}
            />
            {validationError &&
              !validate('email', details.email) &&
              (userList.find((user) => user.email === details.email.toLowerCase()) ? (
                <Typography variant="error">{t('error.emailAdded')}</Typography>
              ) : (
                <Typography variant="error">{t('error.emptyField')}</Typography>
              ))}
            <FormControl>
              <InputLabel>{t('person.modal.role')}</InputLabel>
              <Select id='role-dropdown' label={t('person.modal.role')} value={details.role} onChange={(e) => handleRoleChange(e)}>
                {UserRoles.list().map((role) => (
                  <MenuItem id={role} key={role} value={role}>
                    {t(`person.roles.${role}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Container>
        <Container variant="buttonRow" disableGutters>
          <Stack direction="row" justifyContent="flex-end">
            <Button id='cancel' variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
              {t('person.modal.buttonCancel')}
            </Button>
            <Button
              id='add'
              variant="contained"
              disabled={!details.email}
              startIcon={<AddRoundedIcon />}
              onClick={() => handleSave()}
            >
              {t('person.user.add')}
            </Button>
          </Stack>
        </Container>
      </Container>
    </Modal>
  );
}

export default AddUserModal;
